<script lang="ts" setup>
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Navigation, Pagination, A11y, Autoplay } from 'swiper/modules';
import { Card, CardContent } from '@/components/ui/card';
import { getCmsImage } from '@/composables/useCms';

interface Props {
  title: string;
  heading: string;
  subheading: string;
  body: string;
  images: {
    title: string;
    url: string;
  }[];
  callToAction: {
    text: string;
    customText: string;
    url: string;
  };
}

const props = defineProps<Props>();

// Use custom text if available, otherwise fall back to text
const ctaText = computed(() => {
  return props.callToAction.customText || props.callToAction.text;
});

// Determine if we need a carousel (more than 1 image)
const hasMultipleImages = computed(() => props.images.length > 1);
</script>

<template>
  <VSection>
    <v-container>
      <Card class="overflow-hidden rounded-3xl border-0 bg-white">
        <CardContent class="p-5 lg:pl-16 bg-gray-50">
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-16 items-center">
            <!-- Text Content Column -->
            <div class="flex flex-col gap-6">
              <!-- Subheading -->
              <div v-if="subheading">
                <div class="inline text-sm lg:text-sm font-bold uppercase tracking-wider py-2 px-4 bg-primary-50 rounded">
                  {{ subheading }}
                </div>
              </div>

              <!-- Main Heading -->
              <h2 class="text-2xl lg:text-3xl font-bold text-gray-900 leading-tight">
                {{ heading ?? title }}
              </h2>

              <!-- Body Content -->
              <div
                v-if="body"
                v-html="body"
                class="prose prose-lg text-gray-700 max-w-none prose-headings:text-gray-900 prose-links:text-primary-600"
              />

              <!-- Call to Action Button -->
              <div v-if="callToAction && ctaText" class="flex justify-center lg:justify-start">
                <VButton :href="callToAction.url" class="!px-5 lg:!px-7 !py-4 lg:!py-3 !h-fit !text-base lg:!text-lg font-bold bg-primary text-white rounded-lg">
                  {{ ctaText }}
                </VButton>
              </div>
            </div>

            <!-- Image Column -->
            <div class="relative">
              <!-- Multiple Images: Carousel -->
              <div v-if="hasMultipleImages" class="relative">
                <Swiper
                  :modules="[Navigation, Pagination, A11y, Autoplay]"
                  :slides-per-view="1"
                  :space-between="0"
                  :pagination="{ clickable: true, dynamicBullets: true }"
                  :autoplay="{ delay: 4000, disableOnInteraction: false }"
                  :loop="true"
                  class="rounded-lg overflow-hidden shadow-lg aspect-square"
                >
                  <SwiperSlide v-for="(image, index) in images" :key="`image-${index}`" class="relative w-full h-full">
                    <NuxtImg
                      :src="getCmsImage([image])"
                      :alt="image.title || heading"
                      :title="image.title || heading"
                      format="webp"
                      loading="lazy"
                      class="w-full h-full object-cover"
                    />
                  </SwiperSlide>
                </Swiper>
              </div>

              <!-- Single Image -->
              <div v-else-if="images.length === 1" class="relative">
                <NuxtImg
                  :src="getCmsImage(images)"
                  :alt="images[0].title || heading"
                  :title="images[0].title || heading"
                  format="webp"
                  loading="lazy"
                  class="w-full h-auto rounded-xl lg:aspect-square object-cover"
                />
              </div>

              <!-- Fallback: No Images -->
              <div
                v-else
                class="aspect-[4/3] bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg shadow-lg flex items-center justify-center"
              >
                <div class="text-center text-gray-500">
                  <svg class="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <p class="text-sm font-medium">No image available</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </v-container>
  </VSection>
</template>

<style scoped>
/* Custom carousel pagination styling */
:deep(.swiper-pagination) {
  position: absolute;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  width: auto;
}

:deep(.swiper-pagination-bullet) {
  background-color: rgba(255, 255, 255, 0.6);
  opacity: 1;
  width: 12px;
  height: 12px;
  transition: all 0.3s ease;
}

:deep(.swiper-pagination-bullet-active) {
  background-color: white;
  transform: scale(1.25);
}

/* Ensure proper aspect ratio on all screen sizes */
:deep(.swiper) {
  width: 100%;
}

:deep(.swiper-slide) {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
